/**
 * Eine kleine Teilmenge von Markdown - als Baum, nicht als HTML.
 *
 * Der Unterschied ist der ganze Grund für diese Datei. Übliche
 * Markdown-Bibliotheken erzeugen HTML-Text, und den muss die Oberfläche
 * dann mit `dangerouslySetInnerHTML` einsetzen und vorher säubern, sonst
 * steht irgendwann ein `<script>` in einer Transkription. Hier kommt
 * stattdessen ein Baum heraus, aus dem das Frontend React-Elemente baut.
 * React setzt Text immer als Text ein - es gibt also gar keinen Weg, auf
 * dem Markup hineinkäme, und nichts zu säubern.
 *
 * Was erkannt wird:
 *
 *   # ## ###        Überschriften
 *   > Zitat         auch mehrzeilig, bis sechs Ebenen tief
 *   - / * / +       Aufzählung
 *   1. / 1)         nummerierte Aufzählung, NUR beginnend bei 1
 *   ---             Trennlinie
 *   | a | b |       Tabelle (Kopfzeile und Trennzeile wie in GitHub)
 *   |---|---|
 *   **fett**  *kursiv*  _kursiv_  `wörtlich`
 *   [Text](https://…)
 *
 * Was NICHT erkannt wird, bleibt sichtbar als Text stehen - ein einzelner
 * Stern bleibt ein Stern. Das ist der oberste Grundsatz hier: eine
 * Abschrift darf nichts verlieren. Lieber weniger Formatierung als ein
 * verschlucktes Zeichen.
 *
 * Deshalb beginnt eine nummerierte Liste auch nur bei 1. Sonst würde aus
 * der Zeile „12. März 1834 wurde getauft" eine Liste, die Zahl wäre aus
 * dem Text verschwunden - und der Browser schriebe an ihre Stelle „1.".
 * Eine verfälschte Urkunde ist schlimmer als eine unformatierte.
 *
 * ABWEICHUNG von CommonMark, ebenfalls bewusst: ein einzelner
 * Zeilenumbruch innerhalb eines Absatzes bleibt ein Zeilenumbruch. In
 * Markdown würden die Zeilen zusammenfließen, und genau das zerstört den
 * Zeilenfall einer abgeschriebenen Urkunde.
 */
/** Nur diese Schemata werden zu einem Link. Alles andere bleibt Text. */
const ERLAUBTE_SCHEMATA = ["http://", "https://", "mailto:"];
/**
 * Tiefste Schachtelung von Zitaten.
 *
 * Ohne Grenze baut eine Zeile aus 5000 „>" einen 5000 Ebenen tiefen Baum -
 * das reicht, um beim Durchlaufen den Aufrufstapel zu sprengen und den
 * Browser-Tab mit 5000 verschachtelten Kästchen lahmzulegen. Ab hier
 * bleiben die Zeichen als Text stehen; verloren geht also nichts.
 */
const MAX_ZITATTIEFE = 6;
function istErlaubterLink(url) {
    const u = url.trim().toLowerCase();
    return ERLAUBTE_SCHEMATA.some((s) => u.startsWith(s));
}
const istWortzeichen = (z) => z !== undefined && /[\p{L}\p{N}]/u.test(z);
/**
 * Ein einziger Durchlauf von links nach rechts.
 *
 * Die erste Fassung hat an jeder Stelle alle Zeichen im ganzen Rest neu
 * gesucht - bei 5000 Zeichen aus lauter offenen Klammern waren das fünfeinhalb
 * Sekunden, in denen der Browser steht. Jetzt wird an jeder Stelle nur
 * gefragt „fängt hier etwas an?", und ein Zeichen, für das es kein
 * Gegenstück mehr gibt, wird kein zweites Mal gesucht.
 */
export function inlineLesen(text) {
    const aus = [];
    let puffer = "";
    const spuelen = () => {
        if (!puffer)
            return;
        // Zeilenumbrueche werden eigene Knoten, damit die Anzeige sie nicht
        // erst wieder aus dem Text herausfischen muss.
        const teile = puffer.split("\n");
        teile.forEach((stueck, i) => {
            if (i > 0)
                aus.push({ art: "umbruch" });
            if (stueck)
                aus.push({ art: "text", text: stueck });
        });
        puffer = "";
    };
    /*
     * Erschoepfte Zeichen: findet sich fuer ein Zeichen ab Stelle i kein
     * Gegenstueck mehr, gibt es ab jeder spaeteren Stelle erst recht keines.
     * Dieses Gedaechtnis ist der Unterschied zwischen Millisekunden und
     * Minuten, sobald jemand viele offene Klammern hinschreibt.
     */
    const erschoepft = new Set();
    const findeZu = (zeichen, ab) => {
        if (erschoepft.has(zeichen))
            return -1;
        const pos = text.indexOf(zeichen, ab);
        if (pos < 0)
            erschoepft.add(zeichen);
        return pos;
    };
    let i = 0;
    while (i < text.length) {
        const treffer = konstruktAb(text, i, findeZu);
        if (treffer) {
            spuelen();
            aus.push(treffer.knoten);
            i = treffer.bis;
        }
        else {
            puffer += text[i];
            i++;
        }
    }
    spuelen();
    return aus;
}
/** Faengt an genau dieser Stelle etwas an? */
function konstruktAb(s, i, findeZu) {
    const z = s[i];
    if (z === "`") {
        const zu = findeZu("`", i + 1);
        if (zu < 0 || zu === i + 1)
            return null;
        return { knoten: { art: "woertlich", text: s.slice(i + 1, zu) }, bis: zu + 1 };
    }
    if (z === "[")
        return linkAb(s, i, findeZu);
    if (z === "*" && s[i + 1] === "*") {
        const p = paarAb(s, i, "**", findeZu, false);
        if (p)
            return { knoten: { art: "fett", kinder: inlineLesen(p.inhalt) }, bis: p.bis };
        return null;
    }
    if (z === "*" || z === "_") {
        // Der Unterstrich nur an Wortgrenzen: sonst wird aus der Fundstelle
        // "taufbuch_1834_fol22" mittendrin Kursivschrift.
        const p = paarAb(s, i, z, findeZu, z === "_");
        if (p)
            return { knoten: { art: "kursiv", kinder: inlineLesen(p.inhalt) }, bis: p.bis };
        return null;
    }
    return null;
}
/**
 * Das schliessende Gegenstueck zu einem Hervorhebungszeichen.
 *
 * Direkt hinter dem oeffnenden und direkt vor dem schliessenden Zeichen
 * darf kein Leerraum stehen - sonst waere "a * b * c" eine Hervorhebung
 * statt eines Satzes mit zwei Sternen.
 */
function paarAb(s, i, zeichen, findeZu, wortgrenze) {
    const nachAuf = i + zeichen.length;
    if (nachAuf >= s.length)
        return null;
    if (/\s/.test(s[nachAuf]))
        return null;
    if (wortgrenze && istWortzeichen(s[i - 1]))
        return null;
    let suche = nachAuf;
    for (;;) {
        const zu = findeZu(zeichen, suche);
        if (zu < 0)
            return null;
        const inhalt = s.slice(nachAuf, zu);
        const bis = zu + zeichen.length;
        const taugt = inhalt.length > 0 &&
            !inhalt.includes("\n\n") &&
            !/\s$/.test(inhalt) &&
            (!wortgrenze || !istWortzeichen(s[bis]));
        if (taugt)
            return { inhalt, bis };
        suche = zu + zeichen.length;
    }
}
/**
 * `[Beschriftung](ziel)`.
 *
 * Die Klammern werden gezaehlt, damit eine Matricula-Adresse mit Klammer
 * darin nicht stillschweigend abgeschnitten wird und woandershin zeigt.
 * Taugt das Ziel nicht, kommt `null` zurueck - die eckige Klammer bleibt
 * dann Text, und der Durchlauf geht beim naechsten Zeichen weiter. Ein
 * spaeterer, gueltiger Link wird also trotzdem gefunden.
 */
function linkAb(s, i, findeZu) {
    const zuEckig = findeZu("]", i + 1);
    if (zuEckig < 0)
        return null;
    const beschriftung = s.slice(i + 1, zuEckig);
    if (beschriftung.includes("\n"))
        return null;
    if (s[zuEckig + 1] !== "(")
        return null;
    let tiefe = 1;
    let j = zuEckig + 2;
    for (; j < s.length; j++) {
        const c = s[j];
        if (c === "\n")
            return null;
        if (c === "(")
            tiefe++;
        else if (c === ")") {
            tiefe--;
            if (tiefe === 0)
                break;
        }
    }
    if (tiefe !== 0)
        return null;
    const url = s.slice(zuEckig + 2, j);
    // Leerraum im Ziel waere entweder ein Tippfehler oder ein Versuch, das
    // Schema zu verstecken ("java\tscript:").
    if (url.length === 0 || /\s/.test(url) || !istErlaubterLink(url))
        return null;
    return {
        knoten: { art: "link", url: url.trim(), kinder: inlineLesen(beschriftung || url) },
        bis: j + 1,
    };
}
// ---- Tabellen --------------------------------------------------------------
/**
 * Eine Tabellenzeile in ihre Zellen zerlegen.
 *
 * Von Hand und nicht mit `split`, weil ein `\|` innerhalb einer Zelle ein
 * gewoehnlicher Strich sein soll. Die uebliche Loesung dafuer waere ein
 * Rueckblick im regulaeren Ausdruck - den kennen aeltere Safari-Fassungen
 * nicht, und ein unbekannter Ausdruck ist dort kein stiller Fehler,
 * sondern verhindert das Laden des ganzen Bundles.
 */
function zellenZerlegen(zeile) {
    const zellen = [];
    let aktuell = "";
    for (let i = 0; i < zeile.length; i++) {
        const c = zeile[i];
        if (c === "\\" && zeile[i + 1] === "|") {
            aktuell += "|";
            i++;
            continue;
        }
        if (c === "|") {
            zellen.push(aktuell);
            aktuell = "";
            continue;
        }
        aktuell += c;
    }
    zellen.push(aktuell);
    // Die aeusseren Striche erzeugen leere Randzellen - die gehoeren weg.
    if (zellen.length > 1 && zellen[0].trim() === "")
        zellen.shift();
    if (zellen.length > 1 && zellen[zellen.length - 1].trim() === "")
        zellen.pop();
    return zellen.map((z) => z.trim());
}
const TRENNZELLE = /^:?-{1,}:?$/;
/** Die zweite Zeile einer Tabelle: `|---|:---:|` und Verwandte. */
function istTrennzeile(zeile) {
    if (!zeile || !zeile.includes("|") || !zeile.includes("-"))
        return false;
    const zellen = zellenZerlegen(zeile);
    return zellen.length > 0 && zellen.every((z) => TRENNZELLE.test(z));
}
function ausrichtungLesen(zeile) {
    return zellenZerlegen(zeile).map((z) => {
        const links = z.startsWith(":");
        const rechts = z.endsWith(":");
        if (links && rechts)
            return "mitte";
        if (rechts)
            return "rechts";
        return "links";
    });
}
// ---- Bloecke ---------------------------------------------------------------
const UEBERSCHRIFT = /^(#{1,3})\s+(.*)$/;
const ZITAT = /^>\s?(.*)$/;
const PUNKT = /^[-*+]\s+(.*)$/;
/** Eine Zeile INNERHALB einer nummerierten Liste. */
const NUMMER = /^\d{1,3}[.)]\s+(.*)$/;
/**
 * Womit eine nummerierte Liste beginnen darf - und nur damit.
 *
 * Siehe den Dateikopf: ohne diese Einschraenkung frisst die Liste den Tag
 * aus jedem Datum, das am Zeilenanfang steht.
 */
const NUMMER_START = /^1[.)]\s+/;
const TRENNER = /^(-{3,}|\*{3,}|_{3,})\s*$/;
export function markdownLesen(text, tiefe = 0) {
    if (!text)
        return [];
    // Zeilenenden vereinheitlichen: aus Windows kommt \r\n, und ein \r am
    // Zeilenende macht aus "- " ein unerkanntes Muster.
    const zeilen = text.replace(/\r\n?/g, "\n").split("\n");
    const aus = [];
    const zitatErlaubt = tiefe < MAX_ZITATTIEFE;
    let i = 0;
    const sammeln = (passt) => {
        const gesammelt = [];
        while (i < zeilen.length && passt(zeilen[i], i))
            gesammelt.push(zeilen[i++]);
        return gesammelt;
    };
    /** Beginnt hier eine Tabelle? Das entscheidet erst die naechste Zeile. */
    const tabellenStart = (idx) => zeilen[idx].includes("|") && istTrennzeile(zeilen[idx + 1]);
    while (i < zeilen.length) {
        const zeile = zeilen[i];
        if (zeile.trim() === "") {
            i++;
            continue;
        }
        if (TRENNER.test(zeile)) {
            aus.push({ art: "trenner" });
            i++;
            continue;
        }
        const u = UEBERSCHRIFT.exec(zeile);
        if (u) {
            aus.push({
                art: "ueberschrift",
                stufe: u[1].length,
                inhalt: inlineLesen(u[2].trim()),
            });
            i++;
            continue;
        }
        if (zitatErlaubt && ZITAT.test(zeile)) {
            // Rekursiv: so darf im Zitat alles stehen, was sonst auch gilt -
            // Absaetze, Listen, sogar ein weiteres Zitat.
            const innen = sammeln((z) => ZITAT.test(z));
            aus.push({
                art: "zitat",
                kinder: markdownLesen(innen.map((z) => ZITAT.exec(z)[1]).join("\n"), tiefe + 1),
            });
            continue;
        }
        if (tabellenStart(i)) {
            const kopf = zellenZerlegen(zeile).map((z) => inlineLesen(z));
            const ausrichtung = ausrichtungLesen(zeilen[i + 1]);
            i += 2;
            const roh = sammeln((z) => z.includes("|") && z.trim() !== "");
            aus.push({
                art: "tabelle",
                /*
                 * Eine Kopfzeile aus lauter leeren Zellen kommt oft vor: bei
                 * einem Matrikeneintrag heisst die linke Spalte „Feld" und die
                 * rechte „Lesung", und beides schreibt niemand hin. Dann gibt es
                 * hier auch keine Kopfzeile.
                 */
                kopf: kopf.every((z) => z.length === 0) ? [] : kopf,
                zeilen: roh.map((z) => zellenZerlegen(z).map((c) => inlineLesen(c))),
                ausrichtung,
            });
            continue;
        }
        if (PUNKT.test(zeile) || NUMMER_START.test(zeile)) {
            const nummeriert = NUMMER_START.test(zeile);
            const muster = nummeriert ? NUMMER : PUNKT;
            const roh = sammeln((z) => muster.test(z));
            aus.push({
                art: "liste",
                nummeriert,
                punkte: roh.map((z) => inlineLesen(muster.exec(z)[1])),
            });
            continue;
        }
        /*
         * Ein Absatz laeuft bis zur Leerzeile oder bis etwas anderes beginnt.
         * Beachte NUMMER_START statt NUMMER: eine Zeile wie "12. Maerz 1834"
         * mitten im Absatz soll den Absatz nicht einmal unterbrechen.
         */
        const absatz = sammeln((z, idx) => z.trim() !== "" &&
            !UEBERSCHRIFT.test(z) &&
            !(zitatErlaubt && ZITAT.test(z)) &&
            !PUNKT.test(z) &&
            !NUMMER_START.test(z) &&
            !TRENNER.test(z) &&
            !tabellenStart(idx));
        aus.push({ art: "absatz", inhalt: inlineLesen(absatz.join("\n")) });
    }
    return aus;
}
/**
 * Der Text ohne alle Zeichen - fuer Listenzeilen, Hinweistexte und
 * spaeter die Suche.
 *
 * Nicht einfach die Sonderzeichen wegschneiden, sondern denselben Leser
 * benutzen: sonst wird aus einem Stern, der in der Urkunde steht, still
 * nichts.
 */
export function markdownAlsText(text) {
    const teile = [];
    const inline = (knoten) => {
        for (const k of knoten) {
            if (k.art === "text" || k.art === "woertlich")
                teile.push(k.text);
            else if (k.art === "umbruch")
                teile.push(" ");
            else
                inline(k.kinder);
        }
    };
    const block = (bloecke) => {
        for (const b of bloecke) {
            if (b.art === "trenner")
                continue;
            if (b.art === "zitat")
                block(b.kinder);
            else if (b.art === "liste")
                for (const punkt of b.punkte) {
                    inline(punkt);
                    teile.push(" ");
                }
            else if (b.art === "tabelle")
                for (const zeile of [b.kopf, ...b.zeilen]) {
                    for (const zelle of zeile) {
                        inline(zelle);
                        teile.push(" ");
                    }
                }
            else
                inline(b.inhalt);
            teile.push(" ");
        }
    };
    block(markdownLesen(text));
    return teile.join("").replace(/\s+/g, " ").trim();
}
/** Die erste Zeile, gekuerzt - fuer eine Vorschau in einer Liste. */
export function markdownErsteZeile(text, max = 90) {
    const ganz = markdownAlsText(text);
    if (ganz.length <= max)
        return ganz;
    // An der letzten Wortgrenze davor abschneiden, nicht mitten im Wort.
    const kurz = ganz.slice(0, max);
    const luecke = kurz.lastIndexOf(" ");
    return (luecke > max * 0.6 ? kurz.slice(0, luecke) : kurz) + "…";
}
//# sourceMappingURL=markdown.js.map